import { radius, spacing, ThemeColors, useColors } from "@/constants/theme";
import { getCareerThemes, getRelatedCards } from "@/lib/cardInsights";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useAppStore } from "@/stores/useStore";
import { useRouter } from "expo-router";
import {
  ArrowLeft,
  Briefcase,
  Check,
  ChevronRight,
  ExternalLink,
  PenLine,
  PlayCircle,
  Plus,
} from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  FlatList,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import {
  Button,
  Divider,
  IconButton,
  Snackbar,
  Text,
  TextInput,
} from "react-native-paper";

interface CardDetailProps {
  cardId: string;
  onClose: () => void;
}

export default function CardDetail({ cardId, onClose }: CardDetailProps) {
  const router = useRouter();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const {
    addCardMemo,
    cardMemos,
    cards,
    cardUsageRecords,
    loadCardMemos,
    currentUser,
    login,
    recordCardUsage,
    subjects,
  } = useAppStore();

  const card = cards.find((c) => c.id === cardId);
  const subject = subjects.find((s) => s.id === card?.subject_id);
  const accent = subject?.color_code || c.primary;
  const videoUrls = card?.video_urls?.filter(Boolean) ?? [];
  const cardMemosForCard = cardMemos.filter(
    (m) => m.card_id === card?.id && m.user_id === currentUser?.id,
  );
  const usageRecord = cardUsageRecords.find(
    (r) => r.card_id === card?.id && r.user_id === currentUser?.id,
  );
  const usageCount = usageRecord?.usage_count ?? 0;
  const relatedCards = getRelatedCards(card, cards);
  const careerTags = getCareerThemes(card, subjects);

  useEffect(() => {
    loadCardMemos().catch((error) => {
      console.warn("Failed to load card memos", error);
    });
  }, [loadCardMemos]);

  const [newMemoContent, setNewMemoContent] = useState("");
  const [showMemoForm, setShowMemoForm] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  if (!card) {
    return (
      <View style={styles.container}>
        <Text>カードが見つかりません</Text>
      </View>
    );
  }

  const handleAddMemo = async () => {
    if (!currentUser) {
      if (isSupabaseConfigured) {
        Alert.alert("ログインが必要です", "プロフィールからログインしてください。");
        return;
      }

      try {
        await login();
      } catch (error) {
        console.warn("Failed to login before adding memo", error);
        Alert.alert("ログインできませんでした", "もう一度お試しください。");
      }
      return;
    }

    if (newMemoContent.trim()) {
      try {
        await addCardMemo({
          card_id: card.id,
          user_id: currentUser.id,
          content: newMemoContent,
        });
        setNewMemoContent("");
        setShowMemoForm(false);
      } catch (error) {
        console.warn("Failed to add card memo", error);
        Alert.alert("メモを保存できませんでした", "もう一度お試しください。");
      }
    }
  };

  const handleCardUse = () => {
    if (!currentUser) {
      if (isSupabaseConfigured) {
        Alert.alert("ログインが必要です", "プロフィールからログインしてください。");
        return;
      }

      login().catch((error) => {
        console.warn("Failed to login before recording card use", error);
        Alert.alert("ログインできませんでした", "もう一度お試しください。");
      });
      return;
    }

    recordCardUsage(card.id, card.subject_id)
      .then(() => setSnackbarVisible(true))
      .catch((error) => {
        console.warn("Failed to record card use", error);
      });
  };

  const handlePostWithLens = () => {
    router.push({
      pathname: "/(tabs)/create",
      params: { cardId: card.id },
    });
  };

  const openRelatedCard = (relatedId: string) => {
    router.push({
      pathname: "/cards/[cardId]",
      params: { cardId: relatedId },
    });
  };

  return (
    <View style={styles.root}>
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* ヘッダー */}
      <View
        style={[styles.header, { backgroundColor: subject?.color_code + "20" }]}
      >
        <Pressable onPress={onClose} style={styles.backButton}>
          <ArrowLeft size={24} color={c.textPrimary} />
        </Pressable>

        <View style={styles.headerContent}>
          <View
            style={[
              styles.subjectBadge,
              { backgroundColor: subject?.color_code },
            ]}
          >
            <Text style={styles.subjectBadgeText}>{subject?.name}</Text>
          </View>

          <Text
            variant="headlineSmall"
            style={[styles.cardTitle, { color: subject?.color_code }]}
          >
            {card.title}
          </Text>

          <Text variant="titleSmall" style={styles.lensName}>
            🔍 {card.lens_name}
          </Text>

          {usageCount > 0 && (
            <View style={styles.usagePill}>
              <Check size={13} color={accent} />
              <Text style={[styles.usagePillText, { color: accent }]}>
                あなたはこのレンズを{usageCount}回使いました
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* メインコンテンツ */}
      <View style={styles.content}>
        {/* 定義セクション */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            📖 定義
          </Text>
          <Text style={styles.sectionContent}>{card.definition}</Text>
        </View>

        <Divider style={styles.divider} />

        {/* ツール名 */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            🛠 ツール名
          </Text>
          <Text style={styles.sectionContent}>{card.tool_name}</Text>
        </View>

        <Divider style={styles.divider} />

        {/* 説明セクション */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            💡 説明
          </Text>
          <Text style={styles.sectionContent}>{card.explanation}</Text>
        </View>

        <Divider style={styles.divider} />

        {/* 効果セクション */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            ⚡ 効果
          </Text>
          <Text style={styles.sectionContent}>{card.effect}</Text>
        </View>

        <Divider style={styles.divider} />

        {/* 具体例 */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            📸 具体例
          </Text>
          <Text style={styles.sectionContent}>{card.example}</Text>
        </View>

        <Divider style={styles.divider} />

        {/* 活用例 */}
        {card.application_example && (
          <>
            <View style={styles.section}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                🎯 活用例
              </Text>
              <Text style={styles.sectionContent}>
                {card.application_example}
              </Text>
            </View>
            <Divider style={styles.divider} />
          </>
        )}

        {videoUrls.length > 0 && (
          <>
            <View style={styles.section}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                動画リンク
              </Text>
              <View style={styles.videoLinks}>
                {videoUrls.map((url, index) => (
                  <Pressable
                    accessibilityRole="link"
                    key={`${url}-${index}`}
                    onPress={() => {
                      Linking.openURL(url).catch((error) => {
                        console.warn("Failed to open video URL", error);
                        Alert.alert("リンクを開けませんでした", url);
                      });
                    }}
                    style={({ pressed }) => [
                      styles.videoLink,
                      { borderColor: subject?.color_code || c.primary },
                      pressed && styles.videoLinkPressed,
                    ]}
                  >
                    <PlayCircle size={18} color={subject?.color_code || c.primary} />
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.videoLinkText,
                        { color: subject?.color_code || c.primary },
                      ]}
                    >
                      動画 {index + 1}
                    </Text>
                    <ExternalLink size={15} color={subject?.color_code || c.primary} />
                  </Pressable>
                ))}
              </View>
            </View>
            <Divider style={styles.divider} />
          </>
        )}

        {/* タグ */}
        <View style={styles.section}>
          <Text variant="titleMedium" style={styles.sectionTitle}>
            🏷 思考タイプ
          </Text>
          <View style={styles.tagsContainer}>
            {card.thinking_tags.map((tag) => (
              <View
                key={tag}
                style={[
                  styles.tag,
                  { backgroundColor: accent + "25" },
                ]}
              >
                <Text style={[styles.tagText, { color: accent }]}>
                  {tag}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {careerTags.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                🎓 つながる職業・探究テーマ
              </Text>
              <Text variant="bodySmall" style={styles.sectionHint}>
                このレンズは、こんな分野や仕事の見方につながっています
              </Text>
              <View style={styles.tagsContainer}>
                {careerTags.map((tag) => (
                  <View key={tag} style={styles.careerChip}>
                    <Briefcase size={13} color={c.accent} />
                    <Text style={styles.careerChipText}>{tag}</Text>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}

        {relatedCards.length > 0 && (
          <>
            <Divider style={styles.divider} />
            <View style={styles.section}>
              <Text variant="titleMedium" style={styles.sectionTitle}>
                🔗 関連する学問レンズ
              </Text>
              <Text variant="bodySmall" style={styles.sectionHint}>
                組み合わせると、見え方がさらに広がります
              </Text>
              <View style={styles.relatedList}>
                {relatedCards.map((related) => {
                  const relatedSubject = subjects.find(
                    (s) => s.id === related.subject_id,
                  );
                  const relatedColor =
                    relatedSubject?.color_code || c.primary;
                  return (
                    <Pressable
                      key={related.id}
                      accessibilityRole="button"
                      onPress={() => openRelatedCard(related.id)}
                      style={({ pressed }) => [
                        styles.relatedItem,
                        { borderLeftColor: relatedColor },
                        pressed && styles.relatedItemPressed,
                      ]}
                    >
                      <View style={styles.relatedInfo}>
                        <Text
                          style={[styles.relatedTitle, { color: relatedColor }]}
                        >
                          {related.title}
                        </Text>
                        <Text style={styles.relatedLens} numberOfLines={1}>
                          {relatedSubject?.name}・{related.lens_name}
                        </Text>
                      </View>
                      <ChevronRight size={18} color={c.textMuted} />
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </>
        )}

        <Divider style={styles.divider} />

        {/* マイメモセクション */}
        <View style={styles.section}>
          <View style={styles.memoHeader}>
            <Text variant="titleMedium" style={styles.sectionTitle}>
              📝 マイメモ
            </Text>
            <IconButton
              icon={() => <Plus size={20} />}
              size={24}
              onPress={() => setShowMemoForm(!showMemoForm)}
              style={styles.addMemoButton}
            />
          </View>

          {showMemoForm && (
            <View style={styles.memoForm}>
              <TextInput
                placeholder="ひらめきや発見を記録..."
                value={newMemoContent}
                onChangeText={setNewMemoContent}
                multiline
                numberOfLines={4}
                mode="outlined"
                style={styles.memoInput}
              />
              <View style={styles.memoFormButtons}>
                <Button
                  mode="text"
                  onPress={() => {
                    setShowMemoForm(false);
                    setNewMemoContent("");
                  }}
                >
                  キャンセル
                </Button>
                <Button
                  mode="contained"
                  onPress={handleAddMemo}
                  buttonColor={subject?.color_code}
                >
                  保存
                </Button>
              </View>
            </View>
          )}

          {cardMemosForCard.length > 0 ? (
            <FlatList
              data={cardMemosForCard}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <View style={styles.memoItem}>
                  <Text style={styles.memoContent}>{item.content}</Text>
                  <Text style={styles.memoDate}>
                    {new Date(item.created_at).toLocaleDateString("ja-JP")}
                  </Text>
                </View>
              )}
              scrollEnabled={false}
            />
          ) : (
            <Text style={styles.emptyMemo}>まだメモがありません</Text>
          )}
        </View>

        {/* 使用ボタン */}
        <View style={styles.actionButtons}>
          <Button
            mode="contained"
            onPress={handlePostWithLens}
            buttonColor={accent}
            icon={() => <PenLine size={18} color="#fff" />}
            style={styles.useButton}
          >
            このレンズで発見を投稿
          </Button>
          <Button
            mode="outlined"
            onPress={handleCardUse}
            textColor={accent}
            style={[styles.useButton, { borderColor: accent }]}
          >
            {currentUser ? "使ったレンズとして記録" : "ログインして記録"}
          </Button>
        </View>

        <View style={{ height: 30 }} />
      </View>
    </ScrollView>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={2200}
        style={styles.snackbar}
      >
        🎉 「{card.title}」を使ったレンズに記録しました
      </Snackbar>
    </View>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: c.surface,
  },
  container: {
    flex: 1,
    backgroundColor: c.surface,
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    marginRight: 16,
    padding: 8,
  },
  headerContent: {
    flex: 1,
  },
  subjectBadge: {
    alignSelf: "flex-start",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 8,
  },
  subjectBadgeText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "700",
  },
  cardTitle: {
    fontWeight: "700",
    marginBottom: 4,
  },
  lensName: {
    fontStyle: "italic",
    color: c.textSecondary,
    fontSize: 13,
  },
  usagePill: {
    marginTop: 10,
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: c.surface,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: radius.pill,
  },
  usagePillText: {
    fontSize: 12,
    fontWeight: "700",
  },
  sectionHint: {
    color: c.textMuted,
    marginBottom: spacing.md,
    lineHeight: 16,
  },
  careerChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: c.primarySoft,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: radius.pill,
  },
  careerChipText: {
    fontSize: 12,
    fontWeight: "700",
    color: c.accent,
  },
  relatedList: {
    gap: spacing.sm,
  },
  relatedItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    backgroundColor: c.surfaceAlt,
    borderRadius: radius.md,
    borderLeftWidth: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  relatedItemPressed: {
    opacity: 0.7,
  },
  relatedInfo: {
    flex: 1,
  },
  relatedTitle: {
    fontSize: 14,
    fontWeight: "700",
    marginBottom: 2,
  },
  relatedLens: {
    fontSize: 12,
    color: c.textSecondary,
  },
  snackbar: {
    backgroundColor: c.surfaceAlt,
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    width: "100%",
    maxWidth: 920,
    alignSelf: "center",
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: "700",
    marginBottom: 8,
    color: c.textPrimary,
  },
  sectionContent: {
    lineHeight: 20,
    color: c.textSecondary,
  },
  divider: {
    marginVertical: 16,
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  tag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  tagText: {
    fontSize: 12,
    fontWeight: "600",
  },
  videoLinks: {
    gap: 10,
  },
  videoLink: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: c.surface,
  },
  videoLinkPressed: {
    opacity: 0.72,
  },
  videoLinkText: {
    flex: 1,
    fontSize: 14,
    fontWeight: "700",
  },
  memoHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  addMemoButton: {
    margin: 0,
  },
  memoForm: {
    marginTop: 12,
  },
  memoInput: {
    backgroundColor: c.surfaceAlt,
  },
  memoFormButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
  },
  memoItem: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
  },
  memoContent: {
    fontSize: 14,
    lineHeight: 20,
    color: c.textPrimary,
    marginBottom: 4,
  },
  memoDate: {
    fontSize: 12,
    color: c.textMuted,
  },
  emptyMemo: {
    textAlign: "center",
    color: c.textMuted,
    fontStyle: "italic",
    marginTop: 8,
  },
  actionButtons: {
    marginTop: 24,
    gap: spacing.md,
  },
  useButton: {
    paddingVertical: 6,
  },
});
